import { useEffect } from "react";
import { ShieldAlert, X } from "lucide-react";
import { useSOS } from "../../context/SOSContext";
import { SOSBeaconControl } from "./SOSBeaconControl";
import { SOSIdentityCard } from "./SOSIdentityCard";
import { SOSDirectory } from "./SOSDirectory";
import { SOSStatusQueue } from "./SOSStatusQueue";

/**
 * Fullscreen SOS mode panel. All sub-components are 100% local once the app
 * is loaded: the beacon uses Web Audio, the QR is rendered on canvas, the
 * directory is hardcoded, the queue persists to localStorage.
 *
 * Dismissal: user can close the panel from the X button (if desired), but
 * the data all remains persisted and the panel will re-open automatically on
 * the next `offline` event.
 */
export function SOSMasterPanel() {
  const { open, closePanel, isOnline } = useSOS();

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      id="sos-master-panel"
      data-testid="sos-master-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sos-master-title"
      className="fixed inset-0 z-[130] overflow-y-auto"
      style={{
        background: "linear-gradient(180deg, rgba(60,0,10,0.92) 0%, rgba(15,23,42,0.98) 60%)",
        backdropFilter: "blur(14px)",
      }}
    >
      {/* Status strip */}
      <div
        className="sticky top-0 z-10 w-full px-4 py-2 flex items-center justify-between gap-2"
        style={{
          background: "linear-gradient(90deg, #7f1d1d, #991b1b, #7f1d1d)",
          borderBottom: "1px solid rgba(255,255,255,0.15)",
        }}
        data-testid="sos-status-strip"
      >
        <span className="flex items-center gap-2 text-[11px] sm:text-xs font-mono uppercase tracking-widest font-bold text-white">
          <ShieldAlert size={14} />
          Modo offline activo — Funcionamiento 100% local
        </span>
        <button
          type="button"
          onClick={closePanel}
          data-testid="sos-close"
          aria-label="Cerrar panel SOS"
          className="text-white/90 hover:text-white p-1 rounded-md"
          title={isOnline ? "Cerrar" : "Minimizar (seguirá disponible desde el botón del header)"}
        >
          <X size={16} />
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-5 pb-24 space-y-4">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h1
              id="sos-master-title"
              className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-red-300"
              data-testid="sos-title"
            >
              Modo SOS
            </h1>
            <p className="text-sm text-slate-300 mt-1 max-w-prose">
              Herramientas de emergencia que funcionan sin conexión. Tus datos quedan solo en este dispositivo.
            </p>
          </div>
        </header>

        <SOSBeaconControl />

        <SOSStatusQueue />

        <SOSIdentityCard />

        <SOSDirectory />

        <footer className="pt-2 pb-6 text-center text-[10px] font-mono uppercase tracking-widest text-slate-500">
          Modo SOS · Qué Onda Chat · funciona 100% local una vez cargada la app
        </footer>
      </div>
    </div>
  );
}
